import { useFormat } from "../format";

/**
 * Creates and initializes a currency store in Alpine.js
 * for managing and formatting player's currency.
 *
 * @returns {Object} An object containing the `init` method to initialize the store.
 */
export const useCurrencyStore = () => {
    // Destructure the currency formatting function from a custom hook/module
    const { formatCurrency } = useFormat();

    /**
     * Initializes the Alpine.js currency store.
     * Sets the current currency to the player's configured currency or defaults to Euro.
     *
     * @function
     */
    const init = () => {
        window.Alpine.store("currency", {
            /**
             * The current currency object.
             * Defaults to player's currency if available, otherwise Euro.
             *
             * @type {Object}
             * @property {number} id - Currency ID.
             * @property {string} name - Currency name.
             * @property {string} iso_code - Currency ISO code (e.g., "EUR").
             * @property {string} symbol - Currency symbol (optional).
             */
            current: window.appContext?.Config?.currency?.current ?? {
                id: 1,
                name: "Euro",
                iso_code: window.appContext?.Config?.currency?.default ?? "EUR",
                symbol: "",
            },

            /**
             * Formats a given numeric value according to the current currency.
             *
             * @param {number} v - The numeric value to format.
             * @param {Object} [options] - Optional formatting options.
             * @param {string} [options.currencyType="en-US"] - Locale string for formatting.
             * @param {number} [options.digit=2] - Number of decimal places.
             * @param {string} [options.format="short"] - Format type, can be "short" or "long".
             * @param {boolean} [options.humanize=false] - Whether to use human-readable formatting (e.g., 10K instead of 10000).
             * @param {number} [options.humanizeTreshold=10000] - Threshold above which humanization applies.
             * @returns {string} The formatted currency string.
             */
            format(
                v,
                options = {
                    currencyType: "en-US",
                    digit: 2,
                    format: "short",
                    humanize: false,
                    humanizeTreshold: 10000,
                },
            ) {
                // Merge default options with user-provided options
                const _options = {
                    ...{
                        currencyType: "en-US",
                        digit: 2,
                        format: "short",
                        humanize: false,
                        humanizeTreshold: 10000,
                    },
                    ...options,
                };

                // For custom/sweepstakes currencies (GC, SC), fall back to USD
                // since their iso_code is not a valid ISO 4217 code.
                const currencyCode = this.current.is_custom
                    ? "USD"
                    : (this.current.iso_code ?? "EUR");

                // Return formatted currency string using the external formatCurrency function
                return formatCurrency(v, currencyCode, {
                    ...{
                        currencyType: "en-US",
                        digit: 2,
                        // Only show symbol if format is short and symbol exists
                        symbol:
                            _options.format === "short" && this.current.symbol
                                ? this.current.symbol
                                : "",
                        // Only humanize if options is set and if value is greather or equals to the treshold
                        humanize:
                            _options.humanize && v >= _options.humanizeTreshold,
                    },
                    ...options,
                });
            },
        });
    };

    // Expose the init function for use in other modules/components
    return {
        init,
    };
};
